/**
 * Tooltip portal — para hotspots con clase `.no-hover-elev`.
 *
 * Algunos hotspots (típicamente la pizarra) no se elevan en hover porque su
 * z-index original tiene que mantenerse — si se elevasen, su sprite pasaría
 * por encima de otros (oficial, etc.) rompiendo el orden visual deseado.
 *
 * Pero si el sprite NO se eleva, su tooltip queda atrapado en su contexto
 * de apilamiento y los sprites vecinos lo tapan. Solución: cuando el ratón
 * entra en uno de estos hotspots, clonamos su `.tip` y lo ponemos a nivel
 * de `<body>` con `position: fixed`, así escapa cualquier stacking context.
 *
 * El original se oculta temporalmente para no duplicar visualmente.
 *
 * Uso:
 *   import { initTooltipPortal } from './tooltip-portal';
 *   initTooltipPortal();
 */

const HOTSPOT_SELECTOR = '.hotspot.no-hover-elev';

/** Tipo de placement extraído de las clases del hotspot. */
type Placement = 'left' | 'right' | 'above' | 'below';

interface PortalState {
  el: HTMLElement;
  hotspot: HTMLElement;
  originalTip: HTMLElement;
}

let active: PortalState | null = null;

/** Determina la posición del tooltip según las clases del hotspot. */
function getPlacement(hotspot: HTMLElement): Placement {
  const cls = hotspot.className;
  if (cls.includes('tip-l')) return 'left';
  if (cls.includes('tip-r')) return 'right';
  if (cls.includes('tip-a')) return 'above';
  return 'below';
}

/** Calcula coordenadas absolutas (en viewport) y transform CSS. */
function computePosition(
  hotspot: HTMLElement,
  placement: Placement,
): { x: number; y: number; transform: string } {
  const r = hotspot.getBoundingClientRect();
  const GAP = 14;

  switch (placement) {
    case 'left':
      return { x: r.left - GAP, y: r.top + r.height / 2, transform: 'translate(-100%, -50%)' };
    case 'right':
      return { x: r.right + GAP, y: r.top + r.height / 2, transform: 'translate(0, -50%)' };
    case 'above':
      return { x: r.left + r.width / 2, y: r.top - GAP, transform: 'translate(-50%, -100%)' };
    case 'below':
      return { x: r.left + r.width / 2, y: r.bottom + GAP, transform: 'translate(-50%, 0)' };
  }
}

function showPortal(hotspot: HTMLElement): void {
  // Si ya hay uno activo (por hover muy rápido entre hotspots), lo cerramos primero.
  if (active) hidePortal();

  const original = hotspot.querySelector<HTMLElement>('.tip');
  if (!original) return;

  const placement = getPlacement(hotspot);
  const { x, y, transform } = computePosition(hotspot, placement);

  const portal = original.cloneNode(true) as HTMLElement;
  portal.classList.add('tip-portal');
  portal.style.opacity = '1';
  portal.style.left = `${x}px`;
  portal.style.top = `${y}px`;
  portal.style.transform = transform;

  document.body.appendChild(portal);

  // Ocultar el original para no duplicar visualmente.
  original.style.opacity = '0';
  original.dataset.portaled = '1';

  active = { el: portal, hotspot, originalTip: original };
}

function hidePortal(): void {
  if (!active) return;
  active.el.remove();
  // Restaurar el original
  if (active.originalTip.dataset.portaled) {
    active.originalTip.style.opacity = '';
    delete active.originalTip.dataset.portaled;
  }
  active = null;
}

/**
 * Engancha listeners de mouseenter/mouseleave a todos los hotspots que coincidan
 * con el selector. Idempotente: usa un dataset flag para no duplicar listeners.
 */
export function initTooltipPortal(): void {
  const hotspots = document.querySelectorAll<HTMLElement>(HOTSPOT_SELECTOR);
  hotspots.forEach((h) => {
    if (h.dataset.tooltipPortalAttached === '1') return;
    h.dataset.tooltipPortalAttached = '1';
    h.addEventListener('mouseenter', () => showPortal(h));
    h.addEventListener('mouseleave', () => hidePortal());
  });
}
